import React, { useMemo } from 'react';
import { Plus, Minus, MapPin, Lock } from 'lucide-react';
import NoDataFound from '../common/NoDataFound';
import ApprovalDot from '../common/ApprovalDot';
import Badge from '../common/Badge';
import { Location } from './LocationTypes';
import { GeographyType } from '../../services/geographyTypeService';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

interface LocationListProps {
    locations: Location[];
    loading: boolean;
    onEdit: (location: Location) => void;
    expandedIds: Set<string>;
    onToggleExpand: (id: string) => void;
    geographyTypes?: GeographyType[];
    onWorkflow?: (location: Location) => void;
}

const LocationList: React.FC<LocationListProps> = ({
    locations, loading, onEdit, expandedIds, onToggleExpand, geographyTypes = [], onWorkflow,
}) => {
    const { formatDate } = useEntitySettings();
    const geoTypeNameById = useMemo(() => {
        const m = new Map<number, string>();
        geographyTypes.forEach(gt => m.set(gt.id, gt.name));
        return m;
    }, [geographyTypes]);
    const treeData = useMemo(() => {
        const buildTree = (parentId: string): Location[] => {
            return locations
                .filter(item => item.parentId === parentId || (!item.parentId && parentId === 'root'))
                .map(item => ({
                    ...item,
                    children: buildTree(item.id),
                }));
        };
        return buildTree('root');
    }, [locations]);

    const toggleExpand = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        onToggleExpand(id);
    };

    const LocationRow = ({ node, level = 0 }: { node: Location; level?: number }) => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedIds.has(node.id);
        const paddingLeft = level * 24 + 12;

        return (
            <React.Fragment>
                <div
                    onClick={() => onEdit(node)}
                    className={`tree-list-row${level > 0 ? ' tree-list-row--child' : ''}${(node as any).is_active === 0 || (node as any).is_active === -1 ? ' is-inactive' : ''}`}
                    data-testid={`BTN_location_row_${node.id}_open`}
                >
                    <div
                        className="tree-list-cell tree-list-cell--dot"
                        onClick={e => { e.stopPropagation(); onWorkflow?.(node); }}
                    >
                        <ApprovalDot state={(node.workflow_state ?? 'draft') as any} onClick={() => onWorkflow?.(node)} />
                    </div>

                    <div className="tree-list-cell tree-list-cell--w-40">
                        <span data-testid={`CELL_location_code_${node.id}`}>
                            {(node as any).code ? (node as any).code : '—'}
                        </span>
                    </div>

                    <div
                        className="tree-list-cell tree-list-cell--main"
                        style={{ paddingLeft: `${paddingLeft}px` }}
                    >
                        <button
                            onClick={(e) => toggleExpand(e, node.id)}
                            className={`tree-list-toggle${hasChildren ? '' : ' tree-list-toggle--hidden'}`}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            data-testid={`BTN_location_row_${node.id}_toggle`}
                        >
                            {isExpanded ? <Minus size={10} /> : <Plus size={10} />}
                        </button>

                        <div className="tree-list-row-title">
                            <span className="tree-list-row-name cell-name-lock">
                                {(node as any).workflow_state === 'approved' && (
                                    <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                )}
                                {node.name}
                            </span>
                            {node.description && (
                                <span className="tree-list-row-sub">{node.description}</span>
                            )}
                        </div>
                    </div>

                    <div className="tree-list-cell tree-list-cell--w-48">
                        {node.type === 'PHYSICAL' && node.address ? (
                            <span className="text-slate-500 text-xs flex items-center gap-1">
                                <MapPin size={10} /> {node.address}
                            </span>
                        ) : node.type === 'ABSTRACT' ? (
                            <span className="text-slate-400 text-xs italic">Region/Zone</span>
                        ) : (
                            <span className="text-slate-400 text-xs">—</span>
                        )}
                    </div>

                    <div className="tree-list-cell tree-list-cell--w-48">
                        <div className="flex items-center gap-1 flex-wrap">
                            <Badge variant={node.type === 'ABSTRACT' ? 'info' : 'success'} size="sm">
                                {node.type === 'ABSTRACT' ? 'Abstract' : 'Physical'}
                            </Badge>
                            {node.geography_type_id != null && geoTypeNameById.has(node.geography_type_id) && (
                                <Badge variant="info" size="sm">
                                    {geoTypeNameById.get(node.geography_type_id)}
                                </Badge>
                            )}
                        </div>
                    </div>

                    <div className="tree-list-cell tree-list-cell--w-32">
                        {formatDate(node.establishedDate)}
                    </div>

                </div>

                <div className={`tree-list-children${isExpanded ? ' tree-list-children--open' : ''}`}>
                    <div className="tree-list-children-inner">
                        {hasChildren && node.children?.map(child => (
                            <LocationRow key={child.id} node={child} level={level + 1} />
                        ))}
                    </div>
                </div>
            </React.Fragment>
        );
    };

    return (
        <div className="tree-list">
            <div className="tree-list-header">
                <div className="tree-list-header-cell tree-list-cell--dot" />
                <div className="tree-list-header-cell tree-list-cell--w-40">Code</div>
                <div className="tree-list-header-cell tree-list-header-cell--main">Geography Structure</div>
                <div className="tree-list-header-cell tree-list-cell--w-48">Location</div>
                <div className="tree-list-header-cell tree-list-cell--w-48">Type</div>
                <div className="tree-list-header-cell tree-list-cell--w-32">Established</div>
            </div>

            <div className="tree-list-body">
                {loading ? (
                    <div className="tree-list-body-inner" aria-busy="true" aria-live="polite">
                        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                            <div key={i} className="tree-list-row tree-list-skeleton-row">
                                <div className="tree-list-cell tree-list-cell--dot">
                                    <div className="tree-list-skeleton-bar tree-list-skeleton-bar--dot" />
                                </div>
                                <div className="tree-list-cell tree-list-cell--w-40">
                                    <div className="tree-list-skeleton-bar tree-list-skeleton-bar--code" />
                                </div>
                                <div
                                    className="tree-list-cell tree-list-cell--main"
                                    style={{ paddingLeft: `${(i % 3 === 1 ? 36 : 12)}px` }}
                                >
                                    <div className="tree-list-skeleton-toggle" />
                                    <div className="tree-list-row-title">
                                        <div className="tree-list-skeleton-bar tree-list-skeleton-bar--title" />
                                        <div className="tree-list-skeleton-bar tree-list-skeleton-bar--sub" />
                                    </div>
                                </div>
                                <div className="tree-list-cell tree-list-cell--w-48">
                                    <div className="tree-list-skeleton-bar tree-list-skeleton-bar--pill" />
                                </div>
                                <div className="tree-list-cell tree-list-cell--w-48">
                                    <div className="tree-list-skeleton-bar tree-list-skeleton-bar--pill" />
                                </div>
                                <div className="tree-list-cell tree-list-cell--w-32">
                                    <div className="tree-list-skeleton-bar tree-list-skeleton-bar--sub" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : locations.length === 0 ? (
                    <NoDataFound
                        title="No Geographies Found"
                        message="No geographies have been defined yet. Create one to get started."
                    />
                ) : (
                    <div className="tree-list-body-inner">
                        {treeData.map(node => (
                            <LocationRow key={node.id} node={node} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LocationList;
